import {
  useGetExamEventExamineeByIdQuery,
  useResendCertificateEmailMutation,
} from '@exam-center/graphql';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Form,
  FormTextField,
  Loading,
  Text,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { zodResolver } from '@shared/utils';
import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import { mapExamEventExamineeData } from './mapper';
import {
  type ConfirmResendCertificateFormInput,
  ConfirmResendCertificateFormSchema,
} from './schema';

interface ConfirmResendCertificateModalProps {
  open: boolean;
  onClose: () => void;
  examEventExamineeId: string;
}

export default function ConfirmResendCertificateModal(
  props: Readonly<ConfirmResendCertificateModalProps>,
) {
  const { open: isOpen, onClose, examEventExamineeId } = props;

  const { data: examEventExamineeData, loading: isGettingExamEventExaminee } =
    useGetExamEventExamineeByIdQuery({
      skip: !isOpen || examEventExamineeId === '',
      variables: {
        getExamEventExamineeByIdInput: {
          id: examEventExamineeId,
        },
      },
    });

  const { fullName, email } = useMemo(() => {
    return mapExamEventExamineeData(
      examEventExamineeData?.ecGetExamEventExamineeById.examEventExaminee,
    );
  }, [examEventExamineeData]);

  const formContext = useForm<ConfirmResendCertificateFormInput>({
    resolver: zodResolver(ConfirmResendCertificateFormSchema),
    defaultValues: {
      email,
    },
  });
  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = formContext;

  const { showSnackbar } = useSnackbar();

  const [resendCertificateEmail] = useResendCertificateEmailMutation();

  const handleResendCertificate = async (data: ConfirmResendCertificateFormInput) => {
    await resendCertificateEmail({
      variables: {
        resendCertificateEmailInput: {
          examEventExamineeId,
          email: data.email,
        },
      },
      onCompleted: () => {
        showSnackbar({
          message: 'ส่งหนังสือรับรองเรียบร้อยแล้ว',
          variant: 'success',
        });
        onClose();
      },
      onError: () => {
        showSnackbar({
          message: 'เกิดข้อผิดพลาดในการส่งหนังสือรับรอง',
          variant: 'error',
        });
      },
    });
  };

  const handleResetForm = () => {
    reset();
  };

  useEffect(() => {
    if (email) {
      reset({ email });
    }
  }, [email, reset]);

  return (
    <FormProvider {...formContext}>
      <Dialog
        open={isOpen}
        onClose={onClose}
        maxWidth='sm'
        slotProps={{
          paper: {
            component: Form,
            onSubmit: handleSubmit(handleResendCertificate),
          },
          transition: {
            onExited: handleResetForm,
          },
        }}
      >
        <DialogTitle onClose={onClose}>ยืนยันการส่งหนังสือรับรอง</DialogTitle>
        <DialogContent
          sx={{ display: 'flex', flexDirection: 'column', gap: (theme) => theme.spacing(4) }}
          dividers
        >
          {isGettingExamEventExaminee ? (
            <Loading />
          ) : (
            <>
              <Text>
                ระบบจะดำเนินการส่งหนังสือรับรองผลสอบ
                <br />
                ของ <Text variant='body1Semibold'>“{fullName}”</Text> ไปยังอีเมลผู้ใช้ด้านล่าง
              </Text>
              <FormTextField name='email' label='อีเมลผู้ใช้' />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            size='large'
            variant='outlined'
            color='secondary'
            onClick={onClose}
            disabled={isSubmitting}
          >
            ยกเลิก
          </Button>
          <Button size='large' type='submit' disabled={isSubmitting}>
            ส่งหนังสือรับรอง
          </Button>
        </DialogActions>
      </Dialog>
    </FormProvider>
  );
}
